import * as React from 'react'
import axios from 'axios'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft } from '@fortawesome/pro-light-svg-icons'

import GenericStep from '../generic-step'
import Quote from '../../../../sections/quotes'
import WhatYouGet from '../what-you-get'
import styles from './error-step.module.scss'
import Button from '../../../../components/button'
import ContactCard from '../contact-card'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { ICompanyProps } from '../../../signup-v3/dooerPreOffer'

interface IErrorProps {
  type: 'skv' | 'bank' | 'notYetStarted' | 'notAb' | 'tooNew' | 'otherBank' | 'rejected' | 'expired'
  company: ICompanyProps
  personalNumber?: string | null
  organizationId?: string | null
  reasons?: string[] | null
  bank?: string | null
  initialValues?: {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phoneNumber?: string | null
  }
  onBack?: () => void
  onSuccess: () => void
}

export default function ErrorStep({ type, company, reasons, onBack, onSuccess, initialValues }: IErrorProps) {
  const [subscriptionMailStatus, setSubscriptionMailStatus] = React.useState(null)
  const [formSuccess, setFormSuccess] = React.useState(false)

  const subscribe = (email: string) => {
    return (
      axios
        .post('/api/v1/newsletters/free/subscribe', {
          email,
        })
        // eslint-disable-next-line no-console
        .catch((err) => console.error(err))
    )
  }

  // TODO: Need to decide how to contact dooer
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const contactDooer = (firstName: string, lastName: string, email: string, phone: string, customBank?: string) => {
    return null
  }
  /* eslint-enable @typescript-eslint/no-unused-vars */

  const headings = {
    skv: <FormattedMessage defaultMessage="Något gick snett :(" id="sync-bank.error-step.title-skv" tagName="h1" />,
    bank: <FormattedMessage defaultMessage="Något gick snett :(" id="sync-bank.error-step.title-bank" tagName="h1" />,
    notYetStarted: (
      <FormattedMessage
        defaultMessage="Kul att du vill starta bolag"
        id="sync-bank.error-step.title-not-started"
        tagName="h1"
      />
    ),
    notAb: (
      <FormattedMessage
        defaultMessage="Idag stödjer vi enbart aktiebolag"
        id="sync-bank.error-step.title-not-ab"
        tagName="h1"
      />
    ),
    tooNew: (
      <FormattedMessage defaultMessage="Något gick snett :(" id="sync-bank.error-step.title-too-new" tagName="h1" />
    ),
    otherBank: (
      <FormattedMessage
        defaultMessage="Din bank stöds inte just nu"
        id="sync-bank.error-step.title-rejected"
        tagName="h1"
      />
    ),
    expired: (
      <FormattedMessage defaultMessage="Länken har löpt ut" id="sync-bank.error-step.title-expired" tagName="h1" />
    ),
    rejected: (
      <FormattedMessage
        defaultMessage="Ditt företag har förutsättningar som inte stöds av Dooer"
        id="sync-bank.error-step.title-rejected-not-supported-features"
        tagName="h1"
      />
    ),
  }

  const ledes = {
    skv: (
      <FormattedMessage
        defaultMessage="Eftersom något gick fel i kontakten med Skatteverket eller en bank kan vi tyvärr inte hämta den information vi behöver för att bedöma ditt företag. Men vi kan kontakta dig och gå igenom ditt företag så att du kan få en offert. Fyll bara i formuläret nedan."
        id="sync-bank.error-step.lede-skv"
        tagName="p"
      />
    ),
    bank: (
      <FormattedMessage
        defaultMessage="Eftersom något gick fel i kontakten med Skatteverket eller en bank kan vi tyvärr inte hämta den information vi behöver för att bedöma ditt företag. Men vi kan kontakta dig och gå igenom ditt företag så att du kan få en offert. Fyll bara i formuläret nedan."
        id="sync-bank.error-step.lede-bank"
        tagName="p"
      />
    ),
    notYetStarted: (
      <FormattedHTMLMessage
        defaultMessage="Dooer kan tyvärr inte hjälpa dig med själva uppstarten av ditt bolag, utan ditt aktiebolag måste vara registrerat när vi sätter igång. På <a href='https://www.verksamt.se/momsregistrering_info' target='_blank' rel='noopener noreferrer'>verksamt.se</a> kan du läsa mer om hur du startar ditt nya företag"
        id="sync-bank.error-step.lede-not-started"
        tagName="p"
      />
    ),
    notAb: (
      <FormattedMessage
        defaultMessage="Dooer har för tillfället valt att fokusera på aktiebolag för att kunna erbjuda bokföring av absolut högsta kvalitet. I framtiden kommer vi öppna för andra bolagstyper, men först när vi vet att vi kan garantera en fantastisk produkt."
        id="sync-bank.error-step.lede-not-ab"
        tagName="p"
      />
    ),
    tooNew: (
      <FormattedMessage
        defaultMessage="Eftersom ditt företag är startat nyligen kan vi tyvärr inte ta fram ditt pris automatiskt. Men vi kan kontakta dig och gå igenom ditt företag så att du kan få en offert. Fyll bara i formuläret nedan."
        id="sync-bank.error-step.lede-company-too-new"
        tagName="p"
      />
    ),
    otherBank: (
      <FormattedHTMLMessage
        defaultMessage="Idag kräver Dooer att man använder Swedbank, Sparbankerna, Handelsbanken eller SEB för att vi ska kunna hantera ditt företag. I framtiden kommer vi såklart att se till att stödja fler banker."
        id="sync-bank.error-step.lede-other-bank"
        tagName="p"
      />
    ),
    expired: (
      <FormattedHTMLMessage
        defaultMessage="Länken för att synka har löpt ut, kontakta vår support för att få en ny"
        id="sync-bank.error-step.lede-expired"
        tagName="p"
      />
    ),
    rejected: (
      <FormattedHTMLMessage
        defaultMessage="Dooer är byggt för att kunna hantera de vanligaste förutsättningarna som företag har på ett effektivt sätt. Det finns dock ett antal specialfall som vi idag inte hanterar, i framtiden kommer vi såklart att försöka stödja dessa också."
        id="sync-bank.error-step.lede-rejected"
        tagName="p"
      />
    ),
  }

  const ctas = {
    skv: { defaultMessage: 'Kontakta mig', id: 'sync-bank.error-step.cta-skv' },
    bank: { defaultMessage: 'Kontakta mig', id: 'sync-bank.error-step.cta-bank' },
    notYetStarted: {
      defaultMessage: 'Jag är intresserad av Dooer',
      id: 'sync-bank.error-step.cta-not-started',
    },
    notAb: {
      defaultMessage: 'Jag är intresserad av Dooer',
      id: 'sync-bank.error-step.cta-not-ab',
    },
    tooNew: { defaultMessage: 'Kontakta mig', id: 'sync-bank.error-step.cta-not-ab' },
    otherBank: {
      defaultMessage: 'Jag är intresserad av Dooer',
      id: 'sync-bank.error-step.cta-other-bank',
    },
    expired: {
      defaultMessage: 'Kontakta mig',
      id: 'sync-bank.error-step.cta-other-bank',
    },
    rejected: {
      defaultMessage: 'Jag är intresserad av Dooer',
      id: 'sync-bank.error-step.cta-rejected',
    },
  }

  const labels = {
    skv: {
      defaultMessage: 'Jag vill att ni kontaktar mig så jag kan få en offert',
      id: 'sync-bank.error-step.label-skv',
    },
    bank: {
      defaultMessage: 'Jag vill att ni kontaktar mig så jag kan få en offert',
      id: 'sync-bank.error-step.label-bank',
    },
    notYetStarted: {
      defaultMessage: 'Håll mig uppdaterad om vad som händer på Dooer',
      id: 'sync-bank.error-step.label-not-started',
    },
    notAb: {
      defaultMessage: 'Håll mig uppdaterad om vad som händer på Dooer',
      id: 'sync-bank.error-step.label-not-ab',
    },
    tooNew: { defaultMessage: 'Kontakta mig', id: 'sync-bank.error-step.label-not-ab' },
    otherBank: {
      defaultMessage: 'Håll mig uppdaterad om vad som händer på Dooer',
      id: 'sync-bank.error-step.label-other-bank',
    },
    expired: {
      defaultMessage: 'Jag vill att ni kontaktar mig så jag kan få en offert',
      id: 'sync-bank.error-step.label-other-expired',
    },
    rejected: {
      defaultMessage: 'Håll mig uppdaterad om vad som händer på Dooer',
      id: 'sync-bank.error-step.label-rejected',
    },
  }

  return (
    <GenericStep
      company={company}
      content={
        <div style={{ width: '100%' }}>
          {onBack && (
            <div className={styles.backButtonWrapper}>
              <Button className={styles.backButton} onClick={() => onBack()} variant="link">
                <i>
                  <FontAwesomeIcon icon={faAngleLeft} />{' '}
                </i>
                <FormattedMessage defaultMessage="Tillbaka" id="sync-bank.error-step.back-button" />
              </Button>
            </div>
          )}
          {reasons && (
            <div className={styles.reasons}>
              {reasons.map((reason) => (
                <p className={styles.reason} key={reason}>
                  {reason}
                </p>
              ))}
            </div>
          )}

          <div className={styles.contactCardWrapper}>
            {!formSuccess ? (
              <ContactCard
                clearError={() => setSubscriptionMailStatus(null)}
                ctaText={ctas[type]}
                error={subscriptionMailStatus}
                handleFail={() => setFormSuccess(false)}
                handleSuccess={() => {
                  onSuccess()
                  setFormSuccess(true)
                }}
                includeBank={type === 'otherBank'}
                initialValues={initialValues}
                labelText={labels[type]}
                onComplete={(
                  firstName: string,
                  lastName: string,
                  email: string,
                  phone: string,
                  customBank?: string
                ) => {
                  return Promise.all([
                    subscribe(email),
                    contactDooer(firstName, lastName, email, phone, customBank),
                  ]).then(() => null)
                }}
              />
            ) : (
              <div className={styles.checkmarkWrapper}>
                <i className={styles.checkmark}>
                  <FontAwesomeIcon icon={faCheck} />
                </i>
                <FormattedMessage defaultMessage="Perfekt!" id="sync-bank.step-9.title" tagName="h1" />
                <FormattedMessage
                  defaultMessage="Nu kommer du få epost om Dooers tjänster framöver och få reda på när Dooer är redo för dig och ditt företag."
                  id="sync-bank.step-9.lede"
                  tagName="p"
                />
              </div>
            )}
          </div>
          <Quote variant="extraSmall" />
          <WhatYouGet variant={!company ? 'inverted' : 'regular'} />
        </div>
      }
      header={
        <>
          {headings[type]}
          {ledes[type]}
        </>
      }
    />
  )
}
